'use client';

import type { ComponentProps } from 'react';
import { cn } from '@ifixit/ui/lib/utils';
import { FaIcon } from './icon';
import {
   ModalClose,
   ModalContent,
   ModalDescription,
   ModalPortal,
   ModalRoot,
   ModalTitle,
   ModalTrigger,
} from './modal-dialog';

export function Dialog(props: Omit<ComponentProps<typeof ModalRoot>, 'role'>) {
   return <ModalRoot {...props} />;
}

export function DialogTrigger(props: ComponentProps<typeof ModalTrigger>) {
   return <ModalTrigger data-slot="dialog-trigger" {...props} />;
}

export function DialogPortal(props: ComponentProps<typeof ModalPortal>) {
   return <ModalPortal {...props} />;
}

export function DialogClose(props: ComponentProps<typeof ModalClose>) {
   return <ModalClose data-slot="dialog-close" {...props} />;
}

export function DialogContentPrimitive(props: ComponentProps<typeof ModalContent>) {
   return <ModalContent data-slot="dialog-content" {...props} />;
}

export function DialogContent({
   className,
   children,
   showCloseButton = true,
   closeLabel = 'Close',
   ...props
}: ComponentProps<typeof ModalContent> & {
   showCloseButton?: boolean;
   /** Accessible name for the built-in close button. This package has no i18n,
    * so callers with a translator should pass a translated string. */
   closeLabel?: string;
}) {
   return (
      <DialogPortal>
         <ModalContent
            data-slot="dialog-content"
            className={cn(
               DIALOG_BACKDROP,
               'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[calc(50%+var(--safe-top)/2)] left-[50%] z-dialog grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-2xl border p-6 shadow-lg duration-200 sm:max-w-lg',
               className
            )}
            {...props}
         >
            {children}
            {showCloseButton && (
               <ModalClose
                  data-slot="dialog-close"
                  className="button-ghost button-icon absolute top-1.5 right-1.5 text-gray-500 after:absolute after:-inset-1 hover:text-accent-foreground focus-visible:ring-primary focus-visible:ring-offset-2 active:scale-100 active:bg-accent active:text-accent-foreground"
               >
                  <FaIcon name="xmark" />
                  <span className="sr-only">{closeLabel}</span>
               </ModalClose>
            )}
         </ModalContent>
      </DialogPortal>
   );
}

/** The dimmed page behind a dialog, fading with it. For DialogContentPrimitive too. */
export const DIALOG_BACKDROP =
   'backdrop:bg-black/50 data-[state=open]:backdrop:animate-in data-[state=open]:backdrop:fade-in-0 data-[state=closed]:backdrop:animate-out data-[state=closed]:backdrop:fade-out-0';

export function DialogHeader({ className, ...props }: ComponentProps<'div'>) {
   return (
      <div
         data-slot="dialog-header"
         className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
         {...props}
      />
   );
}

export function DialogFooter({ className, ...props }: ComponentProps<'div'>) {
   return (
      <div
         data-slot="dialog-footer"
         className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
         {...props}
      />
   );
}

export function DialogTitle({ className, ...props }: ComponentProps<typeof ModalTitle>) {
   return (
      <ModalTitle
         data-slot="dialog-title"
         className={cn('text-lg leading-none font-semibold', className)}
         {...props}
      />
   );
}

export function DialogDescription({
   className,
   ...props
}: ComponentProps<typeof ModalDescription>) {
   return (
      <ModalDescription
         data-slot="dialog-description"
         className={cn('text-muted-foreground text-sm', className)}
         {...props}
      />
   );
}
